import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { MapPin, FileText, Video, Hash, Calendar, Users, Clock, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Hero banner — photo CRESA en arrière-plan */}
      <section
        className="relative bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(46, 51, 45, 0.55), rgba(46, 51, 45, 0.75)), url('https://customer-assets.emergentagent.com/job_ilot-communautaire/artifacts/zvl6broe_Photo%20projet%20.jpg')",
          minHeight: '360px',
        }}
        data-testid="projects-hero"
      >
        <div className="container mx-auto px-6 py-20 sm:py-28 text-center">
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#E5E3DB] font-bold mb-3">
            CRESA ASBL — Bushumba, Sud-Kivu
          </span>
          <h1
            className="text-5xl sm:text-6xl font-bold text-white mb-4 drop-shadow-lg"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="projects-title"
          >
            Nos Projets
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            Découvrez nos projets de développement communautaire dans les quatre provinces
            pilotes : Sud-Kivu, Haut-Katanga, Kinshasa et Kongo Central.
          </p>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="projects-page">
        <div className="container mx-auto px-6">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}
          {!loading && projects.length === 0 && (
            <div className="text-center py-12" data-testid="no-projects-message">
              <p className="text-[#5C665A]">Aucun projet disponible pour le moment.</p>
            </div>
          )}
          {!loading && projects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div key={project.project_id} className="card group" data-testid={`project-card-${project.project_id}`}>
                  {project.image_url && (
                    <img 
                      src={project.image_url} 
                      alt={project.title}
                      className="w-full h-48 object-cover rounded-t-lg mb-4 -mx-6 -mt-6"
                    />
                  )}
                  <div className="flex items-center space-x-2 text-[#D87D4A] mb-2">
                    <MapPin size={16} />
                    <span className="text-sm font-medium">{project.province}</span>
                    {project.project_number && (
                      <span className="text-xs font-bold text-[#4A6B48] bg-[#4A6B48]/10 px-2 py-0.5 rounded inline-flex items-center gap-1">
                        <Hash size={11} /> {project.project_number}
                      </span>
                    )}
                  </div>
                  <h3 
                    className="text-xl font-bold text-[#2E332D] mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {project.title}
                  </h3>
                  <p className="text-sm text-[#5C665A] leading-relaxed">
                    {project.description}
                  </p>
                  {(project.execution_location || project.duration || project.target_population || project.funder_name || project.start_date) && (
                    <div className="mt-4 pt-3 border-t border-[#E5E3DB] grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[#5C665A]">
                      {project.execution_location && (
                        <div className="inline-flex items-start gap-1.5"><MapPin size={12} className="mt-0.5 shrink-0 text-[#4A6B48]" /><span><strong>Lieu :</strong> {project.execution_location}</span></div>
                      )}
                      {project.duration && (
                        <div className="inline-flex items-start gap-1.5"><Clock size={12} className="mt-0.5 shrink-0 text-[#4A6B48]" /><span><strong>Durée :</strong> {project.duration}</span></div>
                      )}
                      {project.target_population && (
                        <div className="inline-flex items-start gap-1.5"><Users size={12} className="mt-0.5 shrink-0 text-[#4A6B48]" /><span><strong>Cible :</strong> {project.target_population}</span></div>
                      )}
                      {project.funder_name && (
                        <div className="inline-flex items-start gap-1.5"><Briefcase size={12} className="mt-0.5 shrink-0 text-[#D87D4A]" /><span><strong>Bailleur :</strong> {project.funder_name}</span></div>
                      )}
                      {project.start_date && (
                        <div className="inline-flex items-start gap-1.5 sm:col-span-2"><Calendar size={12} className="mt-0.5 shrink-0 text-[#D87D4A]" /><span><strong>Démarrage :</strong> {new Date(project.start_date).toLocaleDateString('fr-FR')}</span></div>
                      )}
                    </div>
                  )}
                  {project.document_url && (
                    <a
                      href={project.document_url.startsWith('http') ? project.document_url : `${BACKEND_URL}${project.document_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`project-document-${project.project_id}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#4A6B48] hover:text-[#3B5539] hover:underline mr-4"
                    >
                      <FileText size={16} />
                      Consulter le PDF
                    </a>
                  )}
                  {project.video_url && (
                    <a
                      href={project.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`project-video-${project.project_id}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#D87D4A] hover:text-[#C66B39] hover:underline"
                    >
                      <Video size={16} />
                      Voir la vidéo
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
